import { continuousServiceStart, mlcServiceLimit } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Drawer,
  Menu,
  Modal,
  PasswordInput,
  Select,
  TextInput,
} from '@mantine/core';
import {
  IconCopy,
  IconDots,
  IconPlus,
  IconPrinter,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { CardMovements } from '../personnel/CardMovements.js';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import type { PersonnelSource } from '../personnel/source.js';
import { errorCode } from '../personnel/source.js';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { loginMessage, loginSheetPdf, officeOrigin } from './login-message.js';
import { roleLabel, rolesFor, setupErrorText } from './RolesScreen.js';
import classes from './Setup.module.css';
import { type SetupSource, setupErrorCode, setupErrorDetail } from './setup-source.js';
import type {
  DatedRoleItem,
  OfficeUserItem,
  OfficeUserSaved,
  RoleItem,
  TemporaryPasswordDrop,
  TemporaryPasswordResult,
  UserList,
  VesselAssignmentItem,
  VesselOption,
} from './setup-types.js';
import {
  TemporaryPasswordDroppedNotice,
  temporaryPasswordDroppedText,
} from './TemporaryPasswordDropped.js';
import {
  adminRowLocked,
  assignmentsSection,
  newUserAllowed,
  offeredRoles,
  type UserAction,
  type UserActor,
  userActorOf,
  userMenuActions,
  userMenuNote,
  userRowLimits,
} from './user-rights.js';

const PAGE_SIZE = 10;

type T = TFunction;

const current = (row: { validFrom: string; validTo: string | null }, today: string) =>
  row.validFrom <= today && (row.validTo === null || row.validTo >= today);

/** Bugün geçerli ya da başlayacak atamalar; bitmişler gösterilmez. */
const live = (row: { validTo: string | null }, today: string) =>
  row.validTo === null || row.validTo >= today;

export interface UsersTableProps {
  users: OfficeUserItem[];
  roles: RoleItem[];
  vessels: VesselOption[];
  today: string;
  canEdit: boolean;
  canManage: boolean;
  /** İşlemi yapan (ADR 0151 §3); verilmezse satırlar ayrılmaz (eski sunucu). */
  actor?: UserActor | null;
  onAction: (user: OfficeUserItem, action: UserAction) => void;
}

type Column = 'name' | 'companyRoles' | 'assignments' | 'signIn' | 'status';

/** Kullanıcılar tablosu: ad ve e-posta, şirket rolleri, gemi atamaları, giriş ve durum. */
export function UsersTable({
  users,
  roles,
  vessels,
  today,
  canEdit,
  canManage,
  actor = null,
  onAction,
}: UsersTableProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.users.${key}`, options);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);
  const roleName = (id: string) => {
    const role = roles.find((r) => r.id === id);
    return role ? roleLabel(t, role) : '—';
  };
  const vesselName = (id: string) => vessels.find((v) => v.id === id)?.name ?? '—';
  const companyRoles = (user: OfficeUserItem) =>
    user.companyRoles.filter((r) => current(r, today)).map((r) => roleName(r.roleId));
  const assignments = (user: OfficeUserItem) =>
    user.vesselAssignments
      .filter((a) => live(a, today))
      .map((a) => k('assignment', { role: roleName(a.roleId), vessel: vesselName(a.vesselId) }));
  const value: Record<Column, (user: OfficeUserItem) => string | number> = {
    name: (user) => user.fullName,
    companyRoles: (user) => companyRoles(user).join(', '),
    assignments: (user) => assignments(user).join(', '),
    signIn: (user) => (user.totpEnrolled ? 0 : 1),
    status: (user) => (user.active ? 0 : 1),
  };
  const sorted = sortItems(users, sort.dir, value[sort.key]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const page_ = Math.min(page, pages);
  const visible = sorted.slice((page_ - 1) * PAGE_SIZE, page_ * PAGE_SIZE);
  const chips = (items: string[]) =>
    items.length === 0 ? (
      <span className={classes.muted}>{k('none')}</span>
    ) : (
      <span className={classes.chips}>
        {items.slice(0, 2).map((item) => (
          <StatusBadge key={item} tone="neutral">
            {item}
          </StatusBadge>
        ))}
        {items.length > 2 ? (
          <span className={classes.muted}>{k('more', { count: items.length - 2 })}</span>
        ) : null}
      </span>
    );
  return (
    <>
      <section className={table.tableWrap} aria-label={k('table')}>
        <table className={`${table.table} ${classes.tableWide} ${classes.tallRows}`}>
          <colgroup>
            <col className={classes.w22} />
            <col className={classes.w18} />
            <col className={classes.w24} />
            <col className={classes.w16} />
            <col className={classes.w14} />
            <col className={classes.w6} />
          </colgroup>
          <thead>
            <tr>
              {(['name', 'companyRoles', 'assignments', 'signIn', 'status'] as const).map(
                (column) => (
                  <SortableTh
                    key={column}
                    column={column}
                    label={k(`columns.${column}`)}
                    sort={sort}
                    onSort={setSort}
                    {...(column === 'name' ? { className: classes.key } : {})}
                  />
                ),
              )}
              <th scope="col" data-numeric>
                {k('columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((user) => {
              const limits = userRowLimits(user, actor, today);
              const note = userMenuNote(user, { canManage, canEdit }, limits);
              return (
                <tr key={user.id}>
                  <td className={classes.key}>
                    <span className={classes.name}>
                      <strong title={user.fullName}>{user.fullName}</strong>
                      <span title={user.email}>{user.email}</span>
                    </span>
                  </td>
                  <td>{chips(companyRoles(user))}</td>
                  <td>{chips(assignments(user))}</td>
                  <td>
                    <span className={classes.chips}>
                      <StatusBadge tone={user.totpEnrolled ? 'success' : 'neutral'}>
                        {user.totpEnrolled ? k('totpSet') : k('totpMissing')}
                      </StatusBadge>
                      {user.mustChangePassword ? (
                        <StatusBadge tone="warning">{k('mustChange')}</StatusBadge>
                      ) : null}
                    </span>
                  </td>
                  <td>
                    <span className={classes.chips}>
                      <StatusBadge tone={user.active ? 'success' : 'danger'} variant="dot">
                        {user.active ? k('active') : k('disabled')}
                      </StatusBadge>
                      {user.admin ? <StatusBadge tone="info">{k('admin')}</StatusBadge> : null}
                    </span>
                  </td>
                  <td data-numeric>
                    {canEdit || canManage ? (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('actionsOf', { name: user.fullName })}
                          >
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {userMenuActions(user, { canEdit, canManage }, limits).map((action) => (
                            <Menu.Item
                              key={action}
                              disabled={action === 'resetTotp' && !user.totpEnrolled}
                              {...(action === 'disable' ? { color: 'red' } : {})}
                              onClick={() => onAction(user, action)}
                            >
                              {k(`action.${action}`)}
                            </Menu.Item>
                          ))}
                          {/* Sunucunun reddedeceği işlem yerine nedeni (U39; ADR 0151 §3) */}
                          {note ? (
                            <Menu.Label className={classes.menuNote}>
                              {k(`menuNote.${note}`)}
                            </Menu.Label>
                          ) : null}
                        </Menu.Dropdown>
                      </Menu>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <TablePager
        page={page_}
        pages={pages}
        summary={k('showing', {
          from: (page_ - 1) * PAGE_SIZE + 1,
          to: Math.min(page_ * PAGE_SIZE, sorted.length),
          total: sorted.length,
        })}
        label={k('pagination')}
        onPage={setPage}
      />
    </>
  );
}

/** Bir kez gösterilen giriş bilgisi: kişinin adı, giriş e-postası ve geçici parola. */
export interface TemporaryLogin {
  name: string;
  email: string;
  password: string;
  /**
   * Kişi etkin mi: parola sıfırlama etkinleştirmez (ADR 0150 §5). `false` ise panelin üstünde
   * uyarı, hazır mesajda ve kâğıtta "girişiniz hazır" satırı yok. Verilmezse etkin sayılır.
   */
  active?: boolean;
}

/**
 * Geçici parola (ADR 0042 §23) [sorular #248]: yanıtta bir kez gelir, burada bir kez gösterilir ve
 * kopyalanır; pencere kapanınca bellekten de düşer. E-postayla gönderilmez. Birincil iş "Copy
 * message": iki dilli hazır mesaj (ofis adresi, gemi uygulaması, e-posta, parola) panoya gider;
 * "Print" aynı bilgilerle tek sayfalık giriş kâğıdı açar (ortak 2026-10-30 madde 9).
 */
export function TemporaryPasswordPanel({
  name,
  email,
  password,
  active,
  extraNote,
  inactiveNote,
  onDone,
}: TemporaryLogin & {
  /** Ek uyarı (Crew ▸ Login Accounts: ofiste yalnız en yeni parola; gemide senkrona dek eskisi). */
  extraNote?: string;
  /**
   * Kişi devre dışı (Create login ve parola sıfırlama onu etkinleştirmez; ADR 0150 §5): uyarı
   * panelin üstünde, hazır mesajda ve kâğıtta "girişiniz hazır" satırı yok. Verilmezse ve
   * `active === false` ise sıfırlamanın uyarısı (`setup.users.temp.inactive`).
   */
  inactiveNote?: string;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.users.temp.${key}`, options);
  const [copied, setCopied] = useState<'message' | 'password' | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const inactive = inactiveNote ?? (active === false ? k('inactive', { name }) : undefined);
  const info = () => ({
    name,
    email,
    password,
    officeUrl: officeOrigin(),
    ...(inactive ? { ready: false } : {}),
  });
  const copy = (text: string, what: 'message' | 'password') => {
    void navigator.clipboard?.writeText(text).then(() => setCopied(what));
  };
  return (
    <div>
      {inactive ? (
        <p className={classes.notice} data-tone="warning" role="note">
          {inactive}
        </p>
      ) : null}
      <p className={classes.muted}>{k('body', { name })}</p>
      <Button
        size="lg"
        fullWidth
        leftSection={<IconCopy size={20} />}
        onClick={() => copy(loginMessage(i18n, info()), 'message')}
      >
        {copied === 'message' ? k('messageCopied') : k('copyMessage')}
      </Button>
      <div className={classes.secret}>
        <code>{password}</code>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconCopy size={14} />}
          onClick={() => copy(password, 'password')}
        >
          {copied === 'password' ? k('copied') : k('copy')}
        </Button>
      </div>
      {extraNote ? (
        <p className={classes.notice} data-tone="warning" role="note">
          {extraNote}
        </p>
      ) : null}
      <p className={classes.muted}>{k('note')}</p>
      {printError ? (
        <p className={classes.error} role="alert">
          {printError}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button
          variant="default"
          leftSection={<IconPrinter size={16} />}
          onClick={() => {
            setPrintError(null);
            // Kart PDF'leriyle aynı yol: pencere tıklamada açılır, PDF istemcide üretilir
            browserPdfOutput()
              .print('felixmate-login.pdf', () =>
                renderPdf(loginSheetPdf(i18n, info()), { print: true }),
              )
              .catch((error: unknown) => setPrintError(t(pdfErrorKey(errorCode(error)))));
          }}
        >
          {k('print')}
        </Button>
        <Button variant="default" onClick={onDone}>
          {k('done')}
        </Button>
      </div>
    </div>
  );
}

/** Kendi parolasıyla onay penceresi (ADR 0042 §23; MEPC.312 4.3.6 dili); personel kartı da kullanır. */
export function PasswordConfirm({
  title,
  body,
  confirmLabel,
  danger = false,
  result = null,
  resultNote,
  onClose,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  /** Parola sıfırlandı: aynı pencerede geçici parola bir kez gösterilir. */
  result?: TemporaryLogin | null;
  /** Geçici parolanın altındaki ek uyarı (`TemporaryPasswordPanel` `extraNote`). */
  resultNote?: string;
  onClose: () => void;
  /** Hata metni ya da `null` (başarılı). */
  onConfirm: (password: string) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={result ? t('setup.users.temp.title') : title}
      closeOnClickOutside={!result}
    >
      {result ? (
        <TemporaryPasswordPanel
          {...result}
          {...(resultNote ? { extraNote: resultNote } : {})}
          onDone={onClose}
        />
      ) : (
        <form
          onSubmit={async (event: FormEvent) => {
            event.preventDefault();
            if (password === '') return;
            setBusy(true);
            const failure = await onConfirm(password);
            setBusy(false);
            if (failure) setError(failure);
          }}
        >
          <p className={classes.muted}>{body}</p>
          <PasswordInput
            label={t('setup.users.confirm.password')}
            description={t('setup.users.confirm.passwordHelp')}
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          {error ? (
            <p className={classes.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {t('setup.users.confirm.cancel')}
            </Button>
            <Button
              type="submit"
              {...(danger ? { color: 'red' } : {})}
              loading={busy}
              disabled={password === ''}
            >
              {confirmLabel}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * Giriş e-postasını düzeltme formu (ADR 0145): şu anki adres, yeni adres ve işlemi yapanın
 * parolası. Personel kartı da kullanır (`ChangeEmailDialog`).
 */
export function ChangeEmailForm({
  user,
  onClose,
  onConfirm,
}: {
  user: Pick<OfficeUserItem, 'email' | 'fullName'>;
  onClose: () => void;
  /** Hata metni ya da `null` (başarılı). */
  onConfirm: (email: string, password: string) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.users.changeEmail.${key}`, options);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = email.trim() !== '' && password !== '';
  return (
    <form
      onSubmit={async (event: FormEvent) => {
        event.preventDefault();
        if (!ready) return;
        setBusy(true);
        const failure = await onConfirm(email.trim(), password);
        setBusy(false);
        if (failure) setError(failure);
      }}
    >
      <p className={classes.muted}>{k('body', { name: user.fullName })}</p>
      <TextInput label={k('current')} value={user.email} readOnly mb="sm" />
      <TextInput
        label={k('next')}
        description={k('nextHelp')}
        type="email"
        required
        autoComplete="off"
        maxLength={320}
        value={email}
        onChange={(event) => setEmail(event.currentTarget.value)}
        mb="sm"
      />
      <PasswordInput
        label={t('setup.users.confirm.password')}
        description={t('setup.users.confirm.passwordHelp')}
        required
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.currentTarget.value)}
      />
      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {t('setup.users.confirm.cancel')}
        </Button>
        <Button type="submit" loading={busy} disabled={!ready}>
          {k('submit')}
        </Button>
      </div>
    </form>
  );
}

/** "Change login email" penceresi (ADR 0145): Settings ▸ Users ve personel kartı. */
export function ChangeEmailDialog(props: Parameters<typeof ChangeEmailForm>[0]) {
  const { t } = useTranslation();
  return (
    <Modal
      opened
      onClose={props.onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={t('setup.users.changeEmail.title')}
    >
      <ChangeEmailForm {...props} />
    </Modal>
  );
}

interface DatedRowDraft {
  key: string;
  id?: string;
  roleId: string | null;
  vesselId?: string | null;
  validFrom: string | null;
  validTo: string | null;
  /** Yalnız gemi ataması: kontratın son günü ve MLC aşım gerekçesi (ADR 0076 §1, §3). */
  contractEnd?: string | null;
  contractReason?: string;
}

/** Atama satırının MLC hesabı (ADR 0076 §3); sunucu aynı kuralla yeniden denetler. */
export interface AssignmentContract {
  /** Kesintisiz hizmetin başı (terfi ve ardışık görev zinciri kesmez). */
  joinedOn: string;
  /** MLC'ye göre gemide son gün: katılış + 12 ay − 1 gün [MLC 2006 Std A2.5.1 §2(b)]. */
  mlcLimit: string;
  /** Kontrat bitişi MLC sınırını aşıyor. */
  exceeds: boolean;
  /** Aşıyor ve gerekçe yok: kaydetme kapalı. */
  needsReason: boolean;
}

/** `key` satırının MLC sınırı; başlangıç tarihi yoksa `null`. */
export function assignmentContract(
  rows: readonly DatedRowDraft[],
  key: string,
): AssignmentContract | null {
  const dated = rows
    .filter((row): row is DatedRowDraft & { validFrom: string } => row.validFrom !== null)
    .map((row) => ({
      key: row.key,
      vesselId: row.vesselId ?? '',
      roleId: row.roleId ?? '',
      validFrom: row.validFrom,
      validTo: row.validTo,
      contractEnd: row.contractEnd ?? null,
      contractReason: row.contractReason ?? '',
    }))
    .sort((x, y) => x.validFrom.localeCompare(y.validFrom));
  const index = dated.findIndex((row) => row.key === key);
  const row = dated[index];
  if (!row) return null;
  const joinedOn = continuousServiceStart(dated, index);
  const mlcLimit = mlcServiceLimit(joinedOn);
  const exceeds = row.contractEnd !== null && row.contractEnd > mlcLimit;
  return { joinedOn, mlcLimit, exceeds, needsReason: exceeds && row.contractReason.trim() === '' };
}

let rowSeq = 0;
const newKey = () => {
  rowSeq += 1;
  return `row-${rowSeq}`;
};

function toDraft(row: DatedRoleItem | VesselAssignmentItem): DatedRowDraft {
  return {
    key: row.id,
    id: row.id,
    roleId: row.roleId,
    ...('vesselId' in row
      ? {
          vesselId: row.vesselId,
          contractEnd: row.contractEnd ?? null,
          contractReason: row.contractReason ?? '',
        }
      : {}),
    validFrom: row.validFrom,
    validTo: row.validTo,
  };
}

/**
 * Tarihli satırlar: şirket rolleri (gemisiz) ya da gemi atamaları. Yönetici olmayana yönetici
 * rolü sunulmaz (`offeredRoles`; U39, ADR 0151 §3); yönetici rolü taşıyan kayıtlı satır ona salt
 * okunur metin olarak görünür, kaldırılamaz (`adminRowLocked`; ADR 0150 §1). Değişmeyen satır
 * kayıtta aynen gider. Yeni satır yazma yolunda `users.create`'tir: yetkisi olmayana (`canAdd`)
 * Add yerine neden yazılır, kayıtlı atamanın gemisi değişmez (eski satır kapanır, yenisi açılır;
 * ADR 0151 Ek). Rol, tarih ve satır kaldırma `users.edit` ile kalır.
 */
export function DatedRows({
  legend,
  help,
  rows,
  roles,
  vessels,
  addLabel,
  today,
  actor,
  canAdd = true,
  addDenied = '',
  onChange,
}: {
  legend: string;
  help: string;
  rows: DatedRowDraft[];
  roles: RoleItem[];
  vessels?: VesselOption[];
  addLabel: string;
  today: string;
  actor: UserActor | null;
  /** Yeni satır açılabilir mi (`users.create`); verilmezse açık. */
  canAdd?: boolean;
  /** `canAdd` kapalıyken Add yerine yazılan neden. */
  addDenied?: string;
  onChange: (rows: DatedRowDraft[]) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`setup.users.form.${key}`);
  const patch = (key: string, change: Partial<DatedRowDraft>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...change } : row)));
  const roleText = (id: string | null) => {
    const role = roles.find((r) => r.id === id);
    return role ? roleLabel(t, role) : '—';
  };
  return (
    <fieldset className={classes.fieldset}>
      <legend>{legend}</legend>
      <p className={classes.muted}>{help}</p>
      <div className={classes.rows}>
        {rows.map((row, index) =>
          row.id !== undefined && adminRowLocked(row, actor) ? (
            <div key={row.key} className={vessels ? classes.assignment : undefined}>
              <p className={classes.muted}>
                {vessels
                  ? `${vessels.find((vessel) => vessel.id === row.vesselId)?.name ?? '—'} · `
                  : ''}
                {roleText(row.roleId)} · {row.validFrom ? formatDay(row.validFrom) : '—'} –{' '}
                {row.validTo ? formatDay(row.validTo) : '…'}
              </p>
              <p className={classes.muted}>{t('setup.users.drawer.assignmentAdminOnly')}</p>
            </div>
          ) : (
            <div key={row.key} className={vessels ? classes.assignment : undefined}>
              <div className={classes.row} data-roles={vessels ? undefined : true}>
                {vessels ? (
                  <Select
                    label={f('vessel')}
                    data={vessels.map((v) => ({ value: v.id, label: v.name }))}
                    value={row.vesselId ?? null}
                    allowDeselect={false}
                    // Gemi değişikliği yeni satırdır (users.create)
                    disabled={!canAdd && row.id !== undefined}
                    onChange={(value) => patch(row.key, { vesselId: value })}
                    aria-label={`${f('vessel')} ${index + 1}`}
                  />
                ) : null}
                <Select
                  label={f('role')}
                  // Şirket rolü office + any (ADR 0143 §4); gemi ziyareti ataması (ADR 0012 §4)
                  // ofis rolüyle de yapılır, orada bütün roller
                  data={offeredRoles(
                    vessels ? roles : rolesFor(roles, 'company', row.roleId),
                    actor,
                    row.roleId,
                  ).map((r) => ({
                    value: r.id,
                    label: roleLabel(t, r),
                  }))}
                  value={row.roleId}
                  allowDeselect={false}
                  searchable
                  onChange={(value) => patch(row.key, { roleId: value })}
                  aria-label={`${f('role')} ${index + 1}`}
                />
                <DayInput
                  label={f('from')}
                  value={row.validFrom}
                  onChange={(value) => patch(row.key, { validFrom: value })}
                />
                <DayInput
                  label={f('to')}
                  value={row.validTo}
                  placeholder={f('toOpen')}
                  clearable
                  onChange={(value) => patch(row.key, { validTo: value })}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  aria-label={`${f('remove')} ${index + 1}`}
                  onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </div>
              {vessels ? (
                <ContractFields rows={rows} row={row} index={index} onPatch={patch} />
              ) : null}
            </div>
          ),
        )}
      </div>
      {canAdd ? (
        <Button
          mt="sm"
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            onChange([
              ...rows,
              {
                key: newKey(),
                roleId: null,
                ...(vessels
                  ? { vesselId: vessels[0]?.id ?? null, contractEnd: null, contractReason: '' }
                  : {}),
                validFrom: today,
                validTo: null,
              },
            ])
          }
        >
          {addLabel}
        </Button>
      ) : (
        <p className={classes.muted}>{addDenied}</p>
      )}
    </fieldset>
  );
}

/**
 * Kontrat bitişi ve gerekçe (ADR 0076 §3, §5): şirket girer; MLC sınırı katılıştan hesaplanıp
 * yardım metninde gösterilir; aşan bitişte gerekçe alanı açılır ve zorunludur. Engel değil,
 * gerekçeli kayıt: uyarı panoda yine MLC sınırından çıkar.
 */
function ContractFields({
  rows,
  row,
  index,
  onPatch,
}: {
  rows: DatedRowDraft[];
  row: DatedRowDraft;
  index: number;
  onPatch: (key: string, change: Partial<DatedRowDraft>) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.users.form.${key}`, options);
  const contract = assignmentContract(rows, row.key);
  const reason = row.contractReason ?? '';
  return (
    <div className={classes.contractRow}>
      <DayInput
        label={f('contractEnd')}
        value={row.contractEnd ?? null}
        placeholder={f('contractOpen')}
        clearable
        {...(row.validFrom ? { minDate: row.validFrom } : {})}
        onChange={(value) => onPatch(row.key, { contractEnd: value })}
        aria-label={`${f('contractEnd')} ${index + 1}`}
      />
      {contract?.exceeds || reason !== '' ? (
        <TextInput
          label={f('contractReason')}
          {...(contract?.exceeds
            ? { description: f('contractExceeds', { date: formatDay(contract.mlcLimit) }) }
            : {})}
          required={contract?.exceeds ?? false}
          maxLength={500}
          value={reason}
          {...(contract?.needsReason ? { error: f('contractReasonRequired') } : {})}
          onChange={(event) => onPatch(row.key, { contractReason: event.currentTarget.value })}
          aria-label={`${f('contractReason')} ${index + 1}`}
        />
      ) : (
        <p className={classes.contractHint}>
          {contract
            ? f('contractHint', { date: formatDay(contract.mlcLimit) })
            : f('contractHintEmpty')}
        </p>
      )}
    </div>
  );
}

const complete = (rows: DatedRowDraft[], vessels: boolean) =>
  rows.every(
    (r) =>
      r.roleId &&
      r.validFrom &&
      (!vessels || (r.vesselId && !assignmentContract(rows, r.key)?.needsReason)),
  );

const asRoles = (rows: DatedRowDraft[]) =>
  rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    roleId: String(r.roleId),
    validFrom: String(r.validFrom),
    validTo: r.validTo,
  }));

const asAssignments = (rows: DatedRowDraft[]) =>
  rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    vesselId: String(r.vesselId),
    roleId: String(r.roleId),
    validFrom: String(r.validFrom),
    validTo: r.validTo,
    contractEnd: r.contractEnd ?? null,
    contractReason: r.contractReason?.trim() ? r.contractReason.trim() : null,
  }));

function errorOf(t: T, error: unknown): string {
  return setupErrorText(t, setupErrorCode(error), setupErrorDetail(error));
}

/**
 * Gemi atamalarının iyimser kilidi (ADR 0155 K7): çekmecenin yüklediği bütün satırlar (bitmişler
 * dahil) kimlik ve sürümle gider; arada panel, Sign On / Off ya da başka kullanıcı değiştirdiyse
 * sunucu hiçbir şey yazmaz.
 */
export function assignmentsExpected(
  rows: readonly Pick<VesselAssignmentItem, 'id' | 'rowVersion'>[],
): Array<{ id: string; rowVersion: number }> {
  return rows.map((row) => ({ id: row.id, rowVersion: row.rowVersion }));
}

/** Gemi atamalarını kaydetme hatası: çakışma kendi metniyle, diğerleri ortak metinle. */
export function assignmentsErrorText(t: T, error: unknown): string {
  return setupErrorCode(error) === 'ROW_VERSION_CONFLICT'
    ? t('setup.users.drawer.assignmentsConflict')
    : errorOf(t, error);
}

function NewUserDialog({
  source,
  roles,
  vessels,
  today,
  actor,
  onClose,
  onCreated,
}: {
  source: SetupSource;
  roles: RoleItem[];
  vessels: VesselOption[];
  today: string;
  actor: UserActor | null;
  onClose: () => void;
  onCreated: (result: TemporaryPasswordResult) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`setup.users.form.${key}`);
  const [created, setCreated] = useState<TemporaryPasswordResult | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyRoles, setCompanyRoles] = useState<DatedRowDraft[]>([]);
  const [assignments, setAssignments] = useState<DatedRowDraft[]>([]);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready =
    email.trim() !== '' &&
    fullName.trim() !== '' &&
    password !== '' &&
    complete(companyRoles, false) &&
    complete(assignments, true);
  return (
    <Modal
      opened
      onClose={created ? () => onCreated(created) : onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={created ? t('setup.users.temp.title') : f('newTitle')}
      size={created ? 'md' : 'xl'}
      closeOnClickOutside={!created}
    >
      {created ? (
        <TemporaryPasswordPanel
          name={created.user.fullName}
          email={created.user.email}
          password={created.temporaryPassword}
          onDone={() => onCreated(created)}
        />
      ) : (
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            if (!ready) return;
            setBusy(true);
            setError(null);
            try {
              setCreated(
                await source.createUser({
                  email,
                  fullName,
                  companyRoles: asRoles(companyRoles),
                  vesselAssignments: asAssignments(assignments),
                  password,
                }),
              );
              setPassword('');
            } catch (caught) {
              setError(errorOf(t, caught));
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextInput
            label={f('email')}
            description={f('emailHelp')}
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            mb="sm"
          />
          <TextInput
            label={f('fullName')}
            required
            value={fullName}
            maxLength={200}
            onChange={(event) => setFullName(event.currentTarget.value)}
            mb="md"
          />
          <DatedRows
            legend={f('companyRoles')}
            help={f('companyRolesHelp')}
            rows={companyRoles}
            roles={roles}
            addLabel={f('addRole')}
            today={today}
            actor={actor}
            onChange={setCompanyRoles}
          />
          <DatedRows
            legend={f('assignments')}
            help={f('assignmentsHelp')}
            rows={assignments}
            roles={roles}
            vessels={vessels}
            addLabel={f('addAssignment')}
            today={today}
            actor={actor}
            onChange={setAssignments}
          />
          <PasswordInput
            label={t('setup.users.confirm.password')}
            description={t('setup.users.confirm.passwordHelp')}
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          {error ? (
            <p className={classes.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {f('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={!ready}>
              {t('setup.users.confirm.create')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * Tarihli satırların salt okunur listesi: yönetici olmayanın değiştiremediği bölüm (U39; ADR 0151
 * §3) ve kartlı kişinin gemi atamaları (Sign On / Off).
 */
function ReadOnlyRows({
  rows,
  roleText,
  vessels,
}: {
  rows: readonly (DatedRoleItem & { vesselId?: string })[];
  roleText: (id: string) => string;
  vessels?: VesselOption[];
}) {
  return (
    <ul className={classes.muted}>
      {rows.map((row) => (
        <li key={row.id}>
          {vessels ? `${vessels.find((vessel) => vessel.id === row.vesselId)?.name ?? '—'} · ` : ''}
          {roleText(row.roleId)} · {formatDay(row.validFrom)} –{' '}
          {row.validTo ? formatDay(row.validTo) : '…'}
        </li>
      ))}
    </ul>
  );
}

function UserDrawer({
  user,
  source,
  roles,
  vessels,
  today,
  actor,
  canCreate,
  onClose,
  onSaved,
  onChangeEmail,
  onStale,
  personnel,
  onMoved,
}: {
  user: OfficeUserItem;
  source: SetupSource;
  roles: RoleItem[];
  vessels: VesselOption[];
  today: string;
  actor: UserActor | null;
  /** Yeni rol ve atama satırı (`users.create`; ADR 0151 Ek); çekmece `users.edit` ile açılır. */
  canCreate: boolean;
  onClose: () => void;
  /** Rol ya da atama kaydında geçici parolası iptal edilen kişiler de gelir (ADR 0150 §6). */
  onSaved: (user: OfficeUserSaved) => void;
  /** "Change login email" (ADR 0145): çekmece `users.edit` ile açılır. */
  onChangeEmail: () => void;
  /** Kayıt çakıştı (ADR 0155 K7): liste yeniden okunur, kişi yeniden açılınca güncel satırlar. */
  onStale?: () => void;
  /**
   * Kartlı kişinin Sign On / Off işlemleri (ADR 0155 Ek, sahip 2026-11-02 gece): ofisin personel
   * kaynağı; verilmezse bölüm salt okunur kalır (eski davranış).
   */
  personnel?: PersonnelSource | undefined;
  /** Sign On / Off işlemi yazıldı: liste ve kişi yeniden okunur (kişi satırının sürümü değişti). */
  onMoved?: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string) => t(`setup.users.drawer.${key}`);
  const shell = useShell();
  const f = (key: string) => t(`setup.users.form.${key}`);
  const roleText = (id: string) => {
    const role = roles.find((r) => r.id === id);
    return role ? roleLabel(t, role) : '—';
  };
  // Yönetici hesabı ve kişinin kendi rolleri ve atamaları yalnız yöneticiden (U39; ADR 0151 §3)
  const limits = userRowLimits(user, actor, today);
  const section = assignmentsSection(user, actor, today);
  const self = actor !== null && actor.id === user.id;
  const [fullName, setFullName] = useState(user.fullName);
  const [companyRoles, setCompanyRoles] = useState(user.companyRoles.map(toDraft));
  const [assignments, setAssignments] = useState(user.vesselAssignments.map(toDraft));
  // Atamaların yüklendiği küme: kayıtta `expected` olarak gider (ADR 0155 K7)
  const [loadedAssignments, setLoadedAssignments] = useState(user.vesselAssignments);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Yönetici hesabına geçişte iptal edilen geçici parola, kaydedilen bölümün altında (ADR 0150 §6)
  const [dropped, setDropped] = useState<{ key: string; people: TemporaryPasswordDrop[] } | null>(
    null,
  );
  const run = async (key: string, work: () => Promise<OfficeUserSaved>) => {
    setBusy(key);
    setError(null);
    setDropped(null);
    try {
      const saved = await work();
      // Kaydedilen bölüm sunucunun satırlarıyla yenilenir (yeni satırlar kimlik alır)
      if (key === 'roles') setCompanyRoles(saved.companyRoles.map(toDraft));
      if (key === 'assignments') {
        setAssignments(saved.vesselAssignments.map(toDraft));
        setLoadedAssignments(saved.vesselAssignments);
      }
      setDropped({ key, people: saved.temporaryPasswordDropped ?? [] });
      onSaved(saved);
    } catch (caught) {
      setError(key === 'assignments' ? assignmentsErrorText(t, caught) : errorOf(t, caught));
      if (setupErrorCode(caught) === 'ROW_VERSION_CONFLICT') onStale?.();
    } finally {
      setBusy(null);
    }
  };
  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="xl"
      title={d('title')}
      closeButtonProps={{ 'aria-label': d('close') }}
    >
      <fieldset className={classes.fieldset}>
        <legend>{d('details')}</legend>
        <TextInput label={f('email')} value={user.email} readOnly mb={4} />
        {limits.account ? (
          <Button size="compact-xs" variant="subtle" mb="sm" onClick={onChangeEmail}>
            {t('setup.users.action.changeEmail')}
          </Button>
        ) : (
          <p className={classes.muted}>{d('emailAdminOnly')}</p>
        )}
        <TextInput
          label={f('fullName')}
          required
          value={fullName}
          maxLength={200}
          onChange={(event) => setFullName(event.currentTarget.value)}
        />
        <Button
          mt="sm"
          size="xs"
          loading={busy === 'details'}
          disabled={fullName.trim() === '' || fullName === user.fullName}
          onClick={() =>
            void run('details', () => source.updateUser(user.id, user.rowVersion, fullName))
          }
        >
          {d('saveDetails')}
        </Button>
        {user.crewCard && shell ? (
          <Button
            mt="sm"
            ml="xs"
            size="xs"
            variant="light"
            onClick={() => shell.navigate(`/crew/profile/${encodeURIComponent(user.id)}`)}
          >
            {d('openCrewCard')}
          </Button>
        ) : null}
      </fieldset>
      {limits.roles ? (
        <>
          <DatedRows
            legend={f('companyRoles')}
            help={f('companyRolesHelp')}
            rows={companyRoles}
            roles={roles}
            addLabel={f('addRole')}
            today={today}
            actor={actor}
            canAdd={canCreate}
            addDenied={d('addRoleNeedsCreate')}
            onChange={setCompanyRoles}
          />
          <Button
            size="xs"
            mb="md"
            loading={busy === 'roles'}
            disabled={!complete(companyRoles, false)}
            onClick={() =>
              void run('roles', () => source.setCompanyRoles(user.id, asRoles(companyRoles)))
            }
          >
            {d('saveRoles')}
          </Button>
          {dropped?.key === 'roles' ? (
            <TemporaryPasswordDroppedNotice
              dropped={dropped.people}
              className={classes.sectionNotice}
            />
          ) : null}
        </>
      ) : (
        <fieldset className={classes.fieldset}>
          <legend>{f('companyRoles')}</legend>
          <p className={classes.muted}>{d(self ? 'rolesOwn' : 'rolesAdminOnly')}</p>
          <ReadOnlyRows rows={user.companyRoles} roleText={roleText} />
        </fieldset>
      )}
      {section === 'own' || section === 'adminScope' ? (
        // Kendi ataması ve gemi kapsamlı yönetici şirket rolü taşıyan kişinin listesi yalnız
        // yöneticiden; kartlı kişide Sign On / Off'ta da işlem yok (U39; ADR 0150 §1)
        <fieldset className={classes.fieldset}>
          <legend>{f('assignments')}</legend>
          <p className={classes.muted}>
            {d(section === 'own' ? 'assignmentsOwn' : 'assignmentsAdminScope')}
          </p>
          <ReadOnlyRows rows={user.vesselAssignments} roleText={roleText} vessels={vessels} />
        </fieldset>
      ) : section === 'signOnOff' ? (
        // Kartlı kişinin gemi ataması Sign On / Off'tan yazılır (ADR 0131 Ek 2; USE_SIGN_ON_OFF)
        <fieldset className={classes.fieldset}>
          <legend>{f('assignments')}</legend>
          <p className={classes.muted}>{d('assignmentsByCrew')}</p>
          {personnel?.scope === 'web' && personnel.movementEditor ? (
            <CardMovements source={personnel} userId={user.id} onChanged={() => onMoved?.()} />
          ) : (
            <ReadOnlyRows rows={user.vesselAssignments} roleText={roleText} vessels={vessels} />
          )}
          {shell ? (
            <Button size="xs" variant="light" onClick={() => shell.navigate('/crew/sign-on-off')}>
              {d('manageSignOnOff')}
            </Button>
          ) : null}
        </fieldset>
      ) : (
        <>
          <DatedRows
            legend={f('assignments')}
            help={f('assignmentsHelp')}
            rows={assignments}
            roles={roles}
            vessels={vessels}
            addLabel={f('addAssignment')}
            today={today}
            actor={actor}
            canAdd={canCreate}
            addDenied={d('addAssignmentNeedsCreate')}
            onChange={setAssignments}
          />
          <Button
            size="xs"
            loading={busy === 'assignments'}
            disabled={!complete(assignments, true)}
            onClick={() =>
              void run('assignments', () =>
                source.setVesselAssignments(
                  user.id,
                  asAssignments(assignments),
                  assignmentsExpected(loadedAssignments),
                ),
              )
            }
          >
            {d('saveAssignments')}
          </Button>
          {dropped?.key === 'assignments' ? (
            <TemporaryPasswordDroppedNotice
              dropped={dropped.people}
              className={classes.sectionNotice}
            />
          ) : null}
        </>
      )}
      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : null}
    </Drawer>
  );
}

/**
 * Liste okunamadı: yetkisiz kişi (adres elle yazıldı; `users.view` yok) yetki uyarısı görür,
 * diğer hatalarda yeniden deneme (ortak 2026-10-30).
 */
export function UsersLoadProblem({ code, onRetry }: { code: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return code === 'FORBIDDEN' ? (
    <EmptyState
      tone="danger"
      title={t('setup.users.forbiddenTitle')}
      description={t('setup.users.forbiddenText')}
    />
  ) : (
    <LoadError title={t('setup.users.loadError')} code={code} onRetry={onRetry} />
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: UserList; roles: RoleItem[]; vessels: VesselOption[] };
type Notice = { tone: 'success' | 'warning' | 'danger'; text: string };
type Pending = { user: OfficeUserItem; action: Exclude<UserAction, 'edit' | 'changeEmail'> };

/**
 * Kullanıcılar (`/settings/users`; ADR 0042 §23) [sorular #248]: arama ve durum süzgeci, sıralanır
 * ve sayfalanır tablo, "New User" (geçici parola bir kez), düzenleme çekmecesi (ad, şirket
 * rolleri, gemi atamaları), parola ve iki adımlı doğrulama sıfırlama, devre dışı bırakma. Hesap
 * işlemleri işlemi yapanın parolasını ister.
 */
export function UsersScreen({
  source,
  today,
  personnel,
}: {
  source: SetupSource;
  today: string;
  /** Kartlı kişinin Sign On / Off işlemleri çekmecede (ADR 0155 Ek); yalnız web ofis verir. */
  personnel?: PersonnelSource | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.users.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'disabled'>('all');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OfficeUserItem | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [changing, setChanging] = useState<OfficeUserItem | null>(null);
  const [temporary, setTemporary] = useState<TemporaryLogin | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, roles, vessels] = await Promise.all([
        source.users(),
        source.roles().then((r) => r.roles),
        source.vessels(),
      ]);
      setState({ kind: 'ready', list, roles, vessels });
      // Açık çekmece güncel kişiyle sürer (Sign On / Off kişi satırının sürümünü artırır; ADR 0144 §1)
      setEditing((open) => (open ? (list.users.find((u) => u.id === open.id) ?? open) : open));
    } catch (error) {
      setState({ kind: 'error', code: setupErrorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  // İşlemi yapan: yönetici ve kendi satırı ayrılır (U39; ADR 0151 §3)
  const actor = ready ? userActorOf(ready.list) : null;
  const users = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return (ready?.list.users ?? []).filter(
      (user) =>
        (status === 'all' || (status === 'active') === user.active) &&
        (!q ||
          user.fullName.toLocaleLowerCase().includes(q) ||
          user.email.toLocaleLowerCase().includes(q)),
    );
  }, [ready, query, status]);

  const confirmCopy: Record<Pending['action'], { title: string; body: string; confirm: string }> = {
    disable: { title: 'disableTitle', body: 'disableBody', confirm: 'disable' },
    enable: { title: 'enableTitle', body: 'enableBody', confirm: 'enable' },
    resetPassword: {
      title: 'resetPasswordTitle',
      body: 'resetPasswordBody',
      confirm: 'resetPassword',
    },
    resetTotp: { title: 'resetTotpTitle', body: 'resetTotpBody', confirm: 'resetTotp' },
  };

  const act = async (target: Pending, password: string): Promise<string | null> => {
    const { user, action } = target;
    try {
      if (action === 'resetPassword') {
        const result = await source.resetPassword(user.id, user.rowVersion, password);
        // Aynı pencerede bir kez gösterilir; pencere kapanınca bellekten düşer. Sıfırlama devre
        // dışı kişiyi etkinleştirmez (ADR 0150 §5): sonuç bunu söyler
        setTemporary({
          name: result.user.fullName,
          email: result.user.email,
          password: result.temporaryPassword,
          active: result.user.active,
        });
        void load();
        return null;
      } else if (action === 'resetTotp') {
        await source.resetTotp(user.id, password);
        setNotice({ tone: 'success', text: k('totpNotice', { name: user.fullName }) });
      } else if (action === 'disable') {
        await source.disableUser(user.id, user.rowVersion, password);
        setNotice({ tone: 'success', text: k('disabledNotice', { name: user.fullName }) });
      } else {
        await source.enableUser(user.id, user.rowVersion, password);
        setNotice({ tone: 'success', text: k('enabledNotice', { name: user.fullName }) });
      }
      setPending(null);
      void load();
      return null;
    } catch (error) {
      return errorOf(t, error);
    }
  };

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={k('crumb')}
        actions={
          // Kişi açma users.create ve users.edit ister (ADR 0150 Ek)
          ready && newUserAllowed(ready.list) ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              {k('new')}
            </Button>
          ) : undefined
        }
      />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <Panel flush>
        <div className={classes.searchRow}>
          <div className={classes.filters}>
            <TextInput
              className={classes.search}
              leftSection={<IconSearch size={16} aria-hidden />}
              placeholder={k('search')}
              aria-label={k('search')}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <Select
              label={k('status')}
              w={160}
              data={[
                { value: 'all', label: k('statusAll') },
                { value: 'active', label: k('statusActive') },
                { value: 'disabled', label: k('statusDisabled') },
              ]}
              value={status}
              allowDeselect={false}
              onChange={(value) => setStatus((value ?? 'all') as typeof status)}
            />
          </div>
        </div>
        {state.kind === 'error' ? (
          <UsersLoadProblem code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={6} label={k('table')} />
        ) : (ready?.list.users.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : users.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setQuery('');
              setStatus('all');
            }}
          />
        ) : (
          <UsersTable
            users={users}
            roles={ready?.roles ?? []}
            vessels={ready?.vessels ?? []}
            today={today}
            canEdit={ready?.list.canEdit ?? false}
            canManage={ready?.list.canManage ?? false}
            actor={actor}
            onAction={(user, action) => {
              setNotice(null);
              if (action === 'edit') setEditing(user);
              else if (action === 'changeEmail') setChanging(user);
              else setPending({ user, action });
            }}
          />
        )}
      </Panel>
      {creating && ready ? (
        <NewUserDialog
          source={source}
          roles={ready.roles}
          vessels={ready.vessels}
          today={today}
          actor={actor}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setNotice({ tone: 'success', text: k('created', { name: result.user.fullName }) });
            void load();
          }}
        />
      ) : null}
      {editing && ready ? (
        <UserDrawer
          key={editing.id}
          user={editing}
          source={source}
          roles={ready.roles}
          vessels={ready.vessels}
          today={today}
          actor={actor}
          canCreate={ready.list.canCreate}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(saved);
            // Geçici parolası iptal edilen kişi (ADR 0150 §6): çekmece kapanınca da görünür
            const dropped = temporaryPasswordDroppedText(t, saved.temporaryPasswordDropped);
            setNotice(
              dropped
                ? { tone: 'warning', text: `${k('saved', { name: saved.fullName })} ${dropped}` }
                : { tone: 'success', text: k('saved', { name: saved.fullName }) },
            );
            void load();
          }}
          onChangeEmail={() => setChanging(editing)}
          onStale={() => void load()}
          personnel={personnel}
          onMoved={() => void load()}
        />
      ) : null}
      {changing ? (
        <ChangeEmailDialog
          user={changing}
          onClose={() => setChanging(null)}
          onConfirm={async (email, password) => {
            try {
              const saved = await source.changeEmail(
                changing.id,
                changing.rowVersion,
                email,
                password,
              );
              setChanging(null);
              if (editing?.id === saved.id) setEditing(saved);
              setNotice({
                tone: 'success',
                text: k('changeEmail.notice', { name: saved.fullName, email: saved.email }),
              });
              void load();
              return null;
            } catch (error) {
              return errorOf(t, error);
            }
          }}
        />
      ) : null}
      {pending ? (
        <PasswordConfirm
          title={k(`confirm.${confirmCopy[pending.action].title}`)}
          body={k(`confirm.${confirmCopy[pending.action].body}`, { name: pending.user.fullName })}
          confirmLabel={k(`confirm.${confirmCopy[pending.action].confirm}`)}
          danger={pending.action === 'disable'}
          result={pending.action === 'resetPassword' ? temporary : null}
          onClose={() => {
            setPending(null);
            setTemporary(null);
          }}
          onConfirm={(password) => act(pending, password)}
        />
      ) : null}
    </div>
  );
}
